import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '../session'
import { takeVerifier } from '../auth/pkce'
import { isAllowedReturnTo, buildReturnUrl, isProviderCancel } from '../auth/redirect'
import { RETURN_TO_KEY } from './SignInPage'
import { AuthLoading } from '../components/AuthLayout'
import { useResetOnPageRestore } from '@weapps/design'
import { useT } from '../i18n'

// Federation landing (ADR 0052): Cognito redirects here with `?code=` after Google. We hand the code
// + the stashed PKCE verifier to the broker, which completes the token exchange.
export function CallbackPage() {
  const { completeFederated } = useSession()
  const t = useT()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const ran = useRef(false)

  // Backing into this page after the exchange already ran leaves `ran` set and the spinner up with
  // nothing to do; a bfcache restore sends the person back to sign-in instead.
  useResetOnPageRestore(() => navigate('/signin', { replace: true }))

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    void (async () => {
      const code = searchParams.get('code')
      const oauthError = searchParams.get('error_description') || searchParams.get('error')
      // Abandoned Sign-in: the person cancelled at the provider. A choice, not a failure (ADR 0137) —
      // hand the caller its `?auth=cancelled` (the contract it already honours), or return to the chooser
      // with the destination intact. The stashed returnTo is spent either way.
      if (isProviderCancel(searchParams.get('error'))) {
        const stored = sessionStorage.getItem(RETURN_TO_KEY)
        sessionStorage.removeItem(RETURN_TO_KEY)
        takeVerifier()
        const returnTo = stored && isAllowedReturnTo(stored) ? stored : null
        const url = returnTo ? buildReturnUrl(returnTo, 'cancelled') : null
        if (url) window.location.href = url
        else navigate(returnTo ? `/signin?returnTo=${encodeURIComponent(returnTo)}` : '/signin', { replace: true })
        return
      }
      if (oauthError) {
        setError(oauthError)
        return
      }
      const verifier = takeVerifier()
      if (!code || !verifier) {
        setError(t.missingCode)
        return
      }
      try {
        await completeFederated(code, verifier, `${window.location.origin}/callback`)
        const stored = sessionStorage.getItem(RETURN_TO_KEY)
        sessionStorage.removeItem(RETURN_TO_KEY)
        const returnTo = stored && isAllowedReturnTo(stored) ? stored : '/profile'
        // Status-only return callback (ADR 0052) — session rides the bridge cookie, not the URL.
        const url = buildReturnUrl(returnTo, 'success')
        if (url) window.location.href = url
        else navigate(returnTo.startsWith('/') ? returnTo : '/profile', { replace: true })
      } catch (err) {
        setError(err instanceof Error ? err.message : t.signInFailed)
      }
    })()
  }, [completeFederated, navigate, searchParams])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
        <div className="card w-full max-w-sm bg-base-100 shadow-xl">
          <div className="card-body items-center gap-4 text-center">
            <div className="alert alert-error py-2 text-sm"><span>{error}</span></div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/signin', { replace: true })}>
              {t.backToSignIn}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <AuthLoading title={t.signingIn} subtitle={t.signingInSubtitle} />
}
